import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useCreateEntity } from 'src/features/entity/api/useCreateEntity';
import { useGetMembership } from 'src/features/membership/api/useGetMembership';
import { CustomMembershipFormSchemaValues, customMembershipSchema } from 'src/features/membership/formSchema';
import { Membership } from 'src/features/membership/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { cn } from 'src/utils/shadcn';
import { useCreateMembershipType } from 'src/features/membershipType/api/useCreateMembershipType';
import CustomMembershipForm from 'src/features/membership/components/forms/custom/CustomMembershipForm';
import SettingsContext from 'src/features/administration/context/SettingsContext';
import { MEMBERSHIP_TEMPLATE_CATEGORY } from 'src/features/membershipType/types';
import { useUpdateMembershipType } from 'src/features/membershipType/api/useUpdateMembershipType';
import { useGetMembershipType } from 'src/features/membershipType/api/useGetMembershipType';
import { useParking } from 'src/providers/ParkingProvider';

interface CustomMembershipFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMembership?: Membership;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: CustomMembershipFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function CustomMembershipFormModal<TData>({
    selectedMembership,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: CustomMembershipFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const membershipTypeId = React.useMemo(() => selectedMembership?.membership_type.id ?? '', [selectedMembership]);

    const { category = '' } = useParams();
    const { parkingId } = useParking();
    const settingsData = React.useContext(SettingsContext);

    const { data: membership } = useGetMembership({
        parkingId,
        membershipId: selectedMembership?.id ?? '',
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedMembership?.id && isModalOpen },
    });

    const { data: membershipType } = useGetMembershipType({
        parkingId,
        membershipTypeId,
        // We only want to fetch if we're in edit
        config: { enabled: !!membershipTypeId && isModalOpen },
    });

    const { mutate: createMembershipType } = useCreateMembershipType({});
    const { mutate: updateMembershipType } = useUpdateMembershipType({});
    const { mutate: createEntity } = useCreateEntity({});

    const onError = useOnError();

    const defaultValues = React.useMemo(
        () =>
            selectedMembership && membershipType
                ? {
                    ...membershipType,
                    ...selectedMembership,
                    site: membershipType.site.id,
                    limit_to_roles: membershipType.limit_to_roles.map((role) => role.id),
                    membership_type: selectedMembership.membership_type.id,
                    owner: selectedMembership.owner?.id,
                }
                : {
                    membership_type: membershipTypeId,
                    category: MEMBERSHIP_TEMPLATE_CATEGORY.CUSTOM,
                },
        [membershipTypeId, selectedMembership, membershipType],
    );

    const form = useForm<CustomMembershipFormSchemaValues>({
        resolver: zodResolver(customMembershipSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [membership, isModalOpen, category]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmitEntity = React.useCallback((values: CustomMembershipFormSchemaValues)=> {
        const newValues = {
            ...values,
            parkingId,
            translations: settingsData.languages.map((lang) => ({ language: lang, name: values.name })),
            id: membershipTypeId,
        };

        if (!values.membership_type) {
            createMembershipType(newValues, {
                onSuccess: (membershipType) => {
                    mutate(
                        { ...values, membership_type: membershipType.id },
                        () => setIsModalOpen(false),
                        (error) => onError(error, form.setError),
                    );
                }
            })
        } else {
            updateMembershipType(newValues, {
                onSuccess: (membershipType) => {
                    mutate(
                        { ...values, membership_type: membershipType.id },
                        () => setIsModalOpen(false),
                        (error) => onError(error, form.setError),
                    );
                }
            })
        }
    // }, [settingsData, mutate, onError, form.setError]);
    }, [parkingId, membershipTypeId, createMembershipType, mutate, onError, form.setError, updateMembershipType]);

    const onSubmit = React.useCallback(
        (values: CustomMembershipFormSchemaValues) => {
            if (values.entity) {
                createEntity({ parkingId, ...values.entity }, {
                    onSuccess: (entity) => {
                        const newValues = {
                            ...values,
                            owner: entity.id,
                        };
                        onSubmitEntity(newValues);
                    },
                });
            } else onSubmitEntity(values)
        },
        [createEntity, onSubmitEntity, parkingId],
    );


    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild >
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader className="flex-row gap-2">
                    <SquareIcon icon={icon} />
                    <div>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{subtitle}</DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <CustomMembershipForm
                        form={form}
                        onSubmit={onSubmit}
                    />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedMembership && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CustomMembershipFormModal;
